import type { PassioAuth } from "./auth";
import { PassioApiError } from "./errors";
import { PassioRequestHeaders } from "./headers";
import { parseResponseMeta, type PassioResponseMeta } from "./meta";
import type { AvailableModel } from "./models";
import { AffiliatesResource, FeedbackResource, FoodsResource, RecipesResource, ToolsResource, VisionResource } from "./resources";
import { matchPassioRoute } from "./routes";

export type QueryValue = string | number | boolean | null | undefined | Array<string | number | boolean>;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Per-call header overrides (merged over the client defaults). */
  headers?: PassioRequestHeaders | Record<string, string>;
  signal?: AbortSignal;
  /** Skip the `onCall` hook (background lookups that shouldn't show up in inspectors). */
  quiet?: boolean;
  /** Model tag for this call (`?model=`); overrides the client's default model. */
  model?: string;
}

/** Everything about one call, for inspectors and logs. Authorization is redacted. */
export interface PassioCallRecord {
  id: string;
  method: string;
  url: string;
  path: string;
  startedAt: string;
  durationMs: number;
  requestHeaders: Record<string, string>;
  requestBody?: unknown;
  status: number;
  ok: boolean;
  meta?: PassioResponseMeta;
  data?: unknown;
  error?: string;
}

export interface PassioResult<T> {
  data: T;
  meta: PassioResponseMeta;
  status: number;
  record: PassioCallRecord;
}

export interface PassioClientOptions {
  /** Products base, e.g. `https://api.passiolife.com/v2/products` or a proxy such as `/proxy/passio`. */
  baseUrl: string;
  auth: PassioAuth;
  /** Default input headers; a function is re-read on every call (handy for live settings). */
  headers?: PassioRequestHeaders | (() => PassioRequestHeaders);
  fetch?: typeof fetch;
  timeoutMs?: number;
  onCall?: (record: PassioCallRecord) => void;
  /**
   * Model tag for AI routes (sent as `?model=`; empty = the route's own default). A function is
   * called on every AI call with the route id (see `PASSIO_ROUTES`), so each route can use a
   * different model. Tags come from `client.models()`.
   */
  model?: string | ((routeId: string) => string | undefined);
}

const REDACT = new Set(["authorization", "passio-id"]);
let seq = 0;

function buildQuery(query?: Record<string, QueryValue>): string {
  if (!query) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, String(x)));
    else p.append(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export class PassioClient {
  readonly foods: FoodsResource;
  readonly tools: ToolsResource;
  readonly vision: VisionResource;
  readonly recipes: RecipesResource;
  readonly affiliates: AffiliatesResource;
  readonly feedback: FeedbackResource;

  private readonly baseUrl: string;
  private readonly auth: PassioAuth;
  private readonly defaults: () => PassioRequestHeaders;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;
  private readonly onCall?: (record: PassioCallRecord) => void;
  private readonly defaultModel: (routeId: string) => string | undefined;

  constructor(opts: PassioClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.auth = opts.auth;
    const h = opts.headers;
    this.defaults = typeof h === "function" ? h : () => h ?? new PassioRequestHeaders();
    this.fetchImpl = opts.fetch ?? ((...args) => fetch(...args));
    this.timeoutMs = opts.timeoutMs ?? 120_000;
    this.onCall = opts.onCall;
    const m = opts.model;
    this.defaultModel = typeof m === "function" ? m : () => m;
    this.foods = new FoodsResource(this);
    this.tools = new ToolsResource(this);
    this.vision = new VisionResource(this);
    this.recipes = new RecipesResource(this);
    this.affiliates = new AffiliatesResource(this);
    this.feedback = new FeedbackResource(this);
  }

  /** Model tags usable with `?model=` (pass `zdr: true` for zero-data-retention models only). */
  models(params: { zdr?: boolean } = {}, opts?: Omit<RequestOptions, "query" | "body">): Promise<PassioResult<AvailableModel[]>> {
    return this.request("GET", "/napi/models/available", { ...opts, query: { compliance: params.zdr ? "zdr" : undefined } });
  }

  async request<T>(method: "GET" | "POST", path: string, opts: RequestOptions = {}): Promise<PassioResult<T>> {
    const route = matchPassioRoute(method, path);
    const model = opts.model || (route?.ai ? this.defaultModel(route.id) : undefined);
    const url = `${this.baseUrl}${path}${buildQuery({ ...opts.query, model })}`;
    const input =
      opts.headers instanceof PassioRequestHeaders
        ? opts.headers.toHeaders()
        : { ...this.defaults().toHeaders(), ...(opts.headers ?? {}) };
    const hasBody = opts.body !== undefined;
    const started = Date.now();
    const record: PassioCallRecord = {
      id: `${started.toString(36)}-${(++seq).toString(36)}`,
      method,
      url,
      path,
      startedAt: new Date(started).toISOString(),
      durationMs: 0,
      requestHeaders: {},
      requestBody: opts.body,
      status: 0,
      ok: false,
    };

    const timeout = AbortSignal.timeout(this.timeoutMs);
    const signal = opts.signal ? AbortSignal.any([opts.signal, timeout]) : timeout;

    const send = async () => {
      const headers: Record<string, string> = {
        Accept: "application/json",
        ...(hasBody ? { "Content-Type": "application/json" } : {}),
        ...input,
        ...(await this.auth.headers(signal)),
      };
      record.requestHeaders = Object.fromEntries(
        Object.entries(headers).map(([k, v]) => [k, REDACT.has(k.toLowerCase()) ? "•••" : v]),
      );
      return this.fetchImpl(url, {
        method,
        headers,
        body: hasBody ? JSON.stringify(opts.body) : undefined,
        signal,
        credentials: this.auth.credentials,
      });
    };

    try {
      let res = await send();
      if (res.status === 401 && this.auth.invalidate) {
        this.auth.invalidate();
        res = await send();
      }
      const meta = parseResponseMeta(res.headers);
      record.status = res.status;
      record.meta = meta;
      record.durationMs = Date.now() - started;
      if (!res.ok) {
        const err = await PassioApiError.fromResponse(res, meta);
        record.data = err.body;
        record.error = err.message;
        throw err;
      }
      const text = await res.text();
      const data = (text ? JSON.parse(text) : undefined) as T;
      record.ok = true;
      record.data = data;
      record.durationMs = Date.now() - started;
      return { data, meta, status: res.status, record };
    } catch (e) {
      if (!record.error) {
        record.durationMs = Date.now() - started;
        record.error = e instanceof Error ? e.message : String(e);
      }
      if (e instanceof PassioApiError) throw e;
      const aborted = e instanceof Error && (e.name === "AbortError" || e.name === "TimeoutError");
      throw new PassioApiError(aborted ? (opts.signal?.aborted ? "Request cancelled" : "Request timed out") : record.error, {
        status: 0,
        code: aborted ? "aborted" : "network",
      });
    } finally {
      if (!opts.quiet) this.onCall?.(record);
    }
  }
}
